import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

/**
 * DropdownMenu - Three-dot (kebab) menu component with portal rendering
 *
 * @param {Array} actions - Array of action objects with structure:
 *   [
 *     { label: 'Edit', onClick: () => {}, icon: <Edit size={16} />, variant: 'default' },
 *     { label: 'Delete', onClick: () => {}, icon: <Trash2 size={16} />, variant: 'danger' }
 *   ]
 */
const DropdownMenu = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 192, // 192px = w-48 width
      });
    }
  }, [isOpen]);

  const handleActionClick = (action) => {
    action.onClick();
    setIsOpen(false);
  };

  const getButtonStyle = (variant) => {
    switch (variant) {
      case "danger":
        return "hover:bg-red-50 text-red-600";
      case "warning":
        return "hover:bg-yellow-50 text-yellow-600";
      case "success":
        return "hover:bg-green-50 text-green-600";
      default:
        return "hover:bg-gray-100 text-gray-700";
    }
  };

  const dropdownMenu = isOpen ? (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
      }}
    >
      <div className="py-1">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleActionClick(action)}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors duration-150 ${getButtonStyle(
              action.variant,
            )}`}
          >
            {action.icon && (
              <span className="flex-shrink-0">{action.icon}</span>
            )}
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="relative inline-block text-left">
        {/* Three-dot button */}
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors duration-150"
          aria-label="Actions menu"
        >
          <MoreVertical size={20} />
        </button>
      </div>

      {/* Render dropdown using portal to avoid table overflow */}
      {dropdownMenu && createPortal(dropdownMenu, document.body)}
    </>
  );
};

export default DropdownMenu;
